/**
 * IT-работы в теле карточки, решения:
 *  - ItWorksLinesPanel — согласующий решает свои строки этапа модулей (POST /edo/it-works/:id/lines);
 *  - ItWorksFinalChange — финальному утверждающему «Сумма изменена на доработке» (решение 6 от 17.10).
 */
import { useRef, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import type { ItWorksLineDecisionInput, ItWorksLineState, RequestCard } from '../../../lib/types'
import { tone } from '../../../theme/tones'
import { Button, FormTextArea, Tag } from '../../../ui'
import { useMe } from '../../users/useUsers'
import { DiffRows } from '../components/AuditTimeline'
import { errorText } from '../errorText'
import { useDecideItWorksLines } from '../useEdo'
import { itWorksNames, lastRevisedDiff } from './itWorksCard'
import { FOR_WHOM, LINE_STATUS } from './ItWorksModulesTable'

/**
 * Какие строки решать — только сервер (`itWorks.canDecideLines`): свои строки раунда и строки
 * замещаемого, в т.ч. уже решённые — пока этап открыт, решение меняется («Изменить»). Решение —
 * по одной строке за вызов; «На доработку» без комментария не отправить (сервер тоже ответит 400).
 */
export function ItWorksLinesPanel({ card }: { card: RequestCard }) {
  const { t } = useApp()
  const meId = useMe().data?.id
  const decide = useDecideItWorksLines()
  const names = itWorksNames(card)
  const can = new Set(card.itWorks?.canDecideLines)
  const lines = (card.itWorks?.lines ?? []).filter((l) => can.has(l.lineId))
  // Второй клик раньше, чем isPending дойдёт до кнопок, отправил бы второй POST (и, если первый закрыл
  // этап, показал бы 409 «этап закрыт»): защёлка на время запроса.
  const inFlight = useRef(false)
  const send = (d: ItWorksLineDecisionInput, onSuccess: () => void) => {
    if (inFlight.current) return
    inFlight.current = true
    decide.mutate({ id: card.id, decisions: [d] }, { onSuccess, onSettled: () => { inFlight.current = false } })
  }
  return (
    <YStack gap={10} padding={12} borderRadius={7} borderWidth={1} borderColor="$borderSecondary" backgroundColor="$bgLayout">
      <YStack gap={2}>
        <Text fontSize={13} fontWeight="700" color="$color">
          {t({ ru: 'Ваше решение по модулям', en: 'Your decision on modules', az: 'Modullar üzrə qərarınız' })}
        </Text>
        <Text fontSize={12} color="$muted">
          {t({ ru: 'Пока этап модулей открыт, решение можно изменить.', en: 'You can change a decision while the module stage is open.', az: 'Modul mərhələsi açıq olduqca qərarı dəyişmək olar.' })}
        </Text>
      </YStack>
      {lines.map((line) => (
        <LineDecision
          key={line.lineId}
          line={line}
          // строка замещаемого: решаю за него
          forWhom={meId && line.approverId && line.approverId !== meId ? names.get(line.approverId) ?? '—' : undefined}
          pending={decide.isPending}
          send={send}
        />
      ))}
      {decide.error instanceof Error && <Text fontSize={12} color={tone('red').text}>{t(errorText(decide.error))}</Text>}
    </YStack>
  )
}

function LineDecision({
  line,
  forWhom,
  pending,
  send,
}: {
  line: ItWorksLineState
  forWhom?: string
  pending: boolean
  send: (d: ItWorksLineDecisionInput, onSuccess: () => void) => void
}) {
  const { t } = useApp()
  const decided = line.status === 'approved' || line.status === 'rework'
  const [editing, setEditing] = useState(false)
  const [rework, setRework] = useState(false)
  const [comment, setComment] = useState('')
  const reset = () => {
    setEditing(false)
    setRework(false)
    setComment('')
  }
  const cancel = t({ ru: 'Отмена', en: 'Cancel', az: 'Ləğv et' })
  return (
    <YStack gap={8} paddingTop={10} borderTopWidth={1} borderColor="$borderSecondary">
      <XStack alignItems="flex-start" justifyContent="space-between" gap={8}>
        <YStack flexShrink={1}>
          <Text fontSize={13} fontWeight="600" color="$color">
            {line.label ?? '—'}
          </Text>
          {forWhom ? (
            <Text fontSize={12} color="$muted">
              {t(FOR_WHOM)} {forWhom}
            </Text>
          ) : null}
        </YStack>
        {decided ? <Tag tone={LINE_STATUS[line.status].tone}>{t(LINE_STATUS[line.status].label)}</Tag> : null}
      </XStack>
      {decided && line.decision?.comment ? (
        <Text fontSize={12} color="$muted" lineHeight={17}>
          «{line.decision.comment}»
        </Text>
      ) : null}
      {decided && !editing ? (
        <Button size="sm" alignSelf="flex-start" onPress={() => setEditing(true)}>
          {t({ ru: 'Изменить', en: 'Change', az: 'Dəyiş' })}
        </Button>
      ) : rework ? (
        <YStack gap={8}>
          <FormTextArea
            value={comment}
            onChangeText={setComment}
            maxLength={2000}
            placeholder={t({ ru: 'Комментарий (обязателен)', en: 'Comment (required)', az: 'Şərh (məcburi)' })}
          />
          <XStack gap={8} flexWrap="wrap">
            <Button flex={1} minWidth={120} disabled={pending} onPress={() => { setRework(false); setComment('') }}>
              {cancel}
            </Button>
            <Button
              flex={1}
              minWidth={120}
              variant="primary"
              disabled={pending || !comment.trim()}
              opacity={pending || !comment.trim() ? 0.5 : 1}
              onPress={() => comment.trim() && send({ lineId: line.lineId, decision: 'rework', comment: comment.trim() }, reset)}
            >
              {t({ ru: 'Отправить на доработку', en: 'Send for rework', az: 'Düzəlişə göndər' })}
            </Button>
          </XStack>
        </YStack>
      ) : (
        <XStack gap={8} flexWrap="wrap">
          <Button
            flex={1}
            minWidth={120}
            variant="primary"
            disabled={pending}
            opacity={pending ? 0.5 : 1}
            onPress={() => send({ lineId: line.lineId, decision: 'approve' }, reset)}
          >
            {t({ ru: 'Согласовать', en: 'Approve', az: 'Razılaşdır' })}
          </Button>
          <Button flex={1} minWidth={120} disabled={pending} onPress={() => setRework(true)}>
            {t({ ru: 'На доработку', en: 'Rework', az: 'Düzəlişə' })}
          </Button>
          {editing ? (
            <Button flex={1} minWidth={120} onPress={reset}>
              {cancel}
            </Button>
          ) : null}
        </XStack>
      )}
    </YStack>
  )
}

/**
 * Над решением финального утверждающего (держатель открытой задачи IT_FINAL, в т.ч. заместитель):
 * сумма или валюта менялись на доработке — последнее «было / стало» по ним.
 */
export function ItWorksFinalChange({ card }: { card: RequestCard }) {
  const { t } = useApp()
  const meId = useMe().data?.id
  const task = card.tasks?.find((tk) => tk.stage === 'IT_FINAL' && String(tk.status) === 'OPEN')
  const holder = task?.assigneeId
  const mine = !!meId && !!holder && (holder === meId || !!card.actingFor?.includes(holder))
  const diff = mine ? lastRevisedDiff(card.auditEvents, ['amount', 'currency']) : null
  if (!diff) return null
  const c = tone('gold')
  return (
    <YStack gap={2} padding={12} borderRadius={7} backgroundColor={c.soft}>
      <Text fontSize={13} fontWeight="700" color={c.text}>
        {t({ ru: 'Сумма изменена на доработке', en: 'Amount changed during rework', az: 'Məbləğ düzəlişdə dəyişdirilib' })}
      </Text>
      <DiffRows diff={diff} />
    </YStack>
  )
}
